import type { AxiosRequestConfig } from 'axios'
import { http } from '@/api/http'
import { toListParams, type ListQuery } from '@/api/query'
import type { ListResponse } from '@/types/api'

export type RequestOptions = Pick<AxiosRequestConfig, 'signal'>

/** The REST contract every Ready reference resource shares. */
export type CrudService<T, TCreate = Partial<T>, TUpdate = Partial<TCreate>> = {
  /** Path segment under /v1, e.g. "countries". */
  readonly path: string
  list(query?: ListQuery, options?: RequestOptions): Promise<ListResponse<T>>
  get(id: string, options?: RequestOptions): Promise<T>
  create(body: TCreate): Promise<T>
  update(id: string, body: TUpdate): Promise<T>
  remove(id: string): Promise<void>
}

export function createCrudService<T, TCreate = Partial<T>, TUpdate = Partial<TCreate>>(
  path: string,
  options: { updateMethod?: 'put' | 'patch' } = {},
): CrudService<T, TCreate, TUpdate> {
  const base = `/${path}`
  return {
    path,
    async list(query, options) {
      const { data } = await http.get<ListResponse<T>>(base, { params: toListParams(query), ...options })
      return { count: data.count ?? 0, items: data.items ?? [] }
    },
    async get(id, options) {
      const { data } = await http.get<T>(`${base}/${encodeURIComponent(id)}`, options)
      return data
    },
    async create(body) {
      const { data } = await http.post<T>(base, body)
      return data
    },
    async update(id, body) {
      const request = options.updateMethod === 'patch' ? http.patch : http.put
      const { data } = await request<T>(`${base}/${encodeURIComponent(id)}`, body)
      return data
    },
    async remove(id) {
      await http.delete(`${base}/${encodeURIComponent(id)}`)
    },
  }
}
